import React, { PureComponent } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import { InfiniteLoader, List } from 'react-virtualized';
import { RecipeItem } from './RecipeItem';
import { SCROLLING_LIST_HEIGHT, ITEM_HEIGHT } from './constants';

class RecipeItemList extends PureComponent {
  static propTypes = {
    recipeItemProps: PropTypes.object,
    items: PropTypes.array,
    loadNextPage: PropTypes.func,
    droppableId: PropTypes.string,
    height: PropTypes.number,
    isNextPageLoading: PropTypes.bool,
    enableDnD: PropTypes.bool,
    style: PropTypes.object,
  };

  static defaultProps = {
    recipeItemProps: {},
    items: [],
    loadNextPage: () => {},
    height: SCROLLING_LIST_HEIGHT,
    isNextPageLoading: false,
    enableDnD: true,
    style: {},
  };

  infiniteLoaderRef = React.createRef();
  listRef = null;

  state = {
    loadedRowsMap: {},
  };

  // This should only be used for when a new list is provided.
  // A new list would be a new list of results caused by a different search query.
  clearListCache() {
    const { current } = this.infiniteLoaderRef;

    // Sets a scroll offset to use for forcibly re-rendering the list
    if (this.listRef) {
      this.listRef.scrollToPosition(1);
    }

    if (current) {
      current.resetLoadMoreRowsCache();
    }

    this.setState({ loadedRowsMap: {} }, this.forceListLoad);
  }

  forceListLoad() {
    // Force a load to prevent items being stuck in the loading state
    if (this.listRef) {
      this.listRef.scrollToPosition(0);
    }
  }

  onRowsRendered = ({
    overscanStartIndex,
    overscanStopIndex,
    startIndex,
    stopIndex,
  }) => {
    const loadedRowsMap = { ...this.state.loadedRowsMap };

    for (let i = startIndex; i <= stopIndex; i++) {
      loadedRowsMap[i] = true;
    }

    this.setState({ loadedRowsMap });
  };

  get loadMoreRows() {
    const { isNextPageLoading, loadNextPage } = this.props;

    return isNextPageLoading ? () => {} : loadNextPage;
  }

  isRowLoaded = ({ index }) => {
    const { hasNextPage } = this.props;
    const { loadedRowsMap } = this.state;

    return !hasNextPage || !!loadedRowsMap[index];
  };

  renderDragAndDropVersion() {
    const {
      hasNextPage,
      items,
      recipeItemProps,
      droppableId,
      height,
    } = this.props;

    const rowCount = hasNextPage ? items.length + 1 : items.length;

    const rowRenderer = ({ key, index, isScrolling, isVisible, style }) => {
      const item = items[index];

      return (
        <div key={key} style={style}>
          <RecipeItem
            {...recipeItemProps}
            item={item}
            loading={!this.isRowLoaded({ index })}
            index={index}
          />
        </div>
      );
    };

    return (
      <Droppable droppableId={droppableId} isDropDisabled={true}>
        {(provided, snapshot) => (
          <div className="rs-droppable-container" ref={provided.innerRef}>
            <InfiniteLoader
              isRowLoaded={this.isRowLoaded}
              loadMoreRows={this.loadMoreRows}
              rowCount={rowCount}
              ref={this.infiniteLoaderRef}
            >
              {({ onRowsRendered, registerChild }) => (
                <List
                  className="rs-menu-list"
                  ref={ref => {
                    registerChild(ref);
                    this.listRef = ref;
                  }}
                  onRowsRendered={({
                    overscanStartIndex,
                    overscanStopIndex,
                    startIndex,
                    stopIndex,
                  }) => {
                    onRowsRendered({
                      overscanStartIndex,
                      overscanStopIndex,
                      startIndex,
                      stopIndex,
                    });
                    this.onRowsRendered({
                      overscanStartIndex,
                      overscanStopIndex,
                      startIndex,
                      stopIndex,
                    });
                  }}
                  width={334}
                  height={height}
                  rowCount={items.length}
                  rowHeight={ITEM_HEIGHT}
                  rowRenderer={rowRenderer}
                />
              )}
            </InfiniteLoader>
            <div className="rs-droppable-placeholder">
              {provided.placeholder}
            </div>
          </div>
        )}
      </Droppable>
    );
  }

  renderSimpleVersion() {
    const {
      hasNextPage,
      items,
      recipeItemProps,
      height,
      enableDnD,
      style,
    } = this.props;

    const rowCount = hasNextPage ? items.length + 1 : items.length;

    const rowRenderer = ({ key, index, isScrolling, isVisible, style }) => {
      const item = items[index];

      return (
        <div key={key} style={style}>
          <RecipeItem
            {...recipeItemProps}
            item={item}
            loading={!this.isRowLoaded({ index })}
            index={index}
            enableDnD={enableDnD}
          />
        </div>
      );
    };

    return (
      <div className="rs-droppable-container" style={style}>
        <InfiniteLoader
          isRowLoaded={this.isRowLoaded}
          loadMoreRows={this.loadMoreRows}
          rowCount={rowCount}
          ref={this.infiniteLoaderRef}
        >
          {({ onRowsRendered, registerChild }) => (
            <List
              className="rs-menu-list"
              ref={ref => {
                registerChild(ref);
                this.listRef = ref;
              }}
              onRowsRendered={({
                overscanStartIndex,
                overscanStopIndex,
                startIndex,
                stopIndex,
              }) => {
                onRowsRendered({
                  overscanStartIndex,
                  overscanStopIndex,
                  startIndex,
                  stopIndex,
                });
                this.onRowsRendered({
                  overscanStartIndex,
                  overscanStopIndex,
                  startIndex,
                  stopIndex,
                });
              }}
              width={334}
              height={height}
              rowCount={items.length}
              rowHeight={ITEM_HEIGHT}
              rowRenderer={rowRenderer}
            />
          )}
        </InfiniteLoader>
      </div>
    );
  }

  render() {
    const { enableDnD } = this.props;

    if (enableDnD) {
      return this.renderDragAndDropVersion();
    }
    return this.renderSimpleVersion();
  }
}

export { RecipeItemList };



// WEBPACK FOOTER //
// ./src/common/Components/RecipeSearch/RecipeItemList.js